/**
 * CAOS-1916 — a control named INSIDE a sentence of guidance prose.
 *
 * A guide that says "click the grid icon at the left of the tab row" makes the reader hunt for the
 * grid icon. A guide that draws the grid icon at the word "grid" does not. The reference platform
 * built exactly this (UI-523) and the parity audit on CAOS-1915 found CAOS could not express it: a
 * recipe step could name one anchor and the host drew it as a separate line AFTER the prose, and a
 * topic — the type that carries actual paragraphs — could name no anchor at all.
 *
 * The vocabulary is one token: `{anchor:<role>}`, written anywhere in a `guidanceTopic`'s `body` or
 * a step's `text`, any number of times. It resolves at view time against the anchors the org has
 * installed, drawing that anchor's own icon in place.
 *
 * THIS IS NOT A MARKUP CHANNEL, and the distinction is the whole reason it is shaped this way.
 * `shell-frame.ts` sets guidance prose with `textContent` on purpose — "an article is PROSE and not
 * a way to put markup into the frame. A topic is installed metadata and a package is not the host's
 * author." That decision stands. A token here cannot carry a tag, an attribute, a URL or a style; it
 * carries a ROLE, and the only thing the host will draw for it is an icon already registered by an
 * installed `guidanceAnchor`. A package can point at the interface; it still cannot author the frame.
 *
 * WHY ITS OWN MODULE. Two readers need the same grammar and must not drift: the panel, which splits
 * prose to draw it, and the panel's SEARCH, which must strip tokens so that searching for two words
 * either side of a glyph still matches. That is the same "one script, two readers" reasoning
 * `guidance-articles.ts` is a separate module for, and the same failure it avoids — a later change
 * to the rendering grammar silently becoming a change to what is searchable.
 *
 * WHY THE TOKEN NAMES A ROLE AND NOT A CONTROL. The reference's token keys are a closed enum of six
 * baked into its panel, so a new control means editing the panel. An anchor is installed metadata
 * with a role, a label, an icon and a region, contributed by whichever package provides the surface;
 * naming the role keeps the existing portability property — a recipe never changes when the
 * interface does — and lets a package point at its own controls without the host knowing they exist.
 */

/**
 * One token. The role runs to the closing brace rather than matching a restricted character class,
 * because a role legitimately contains a colon: the anchor model's own examples include
 * `object-nav:<object>`. A character class narrow enough to look tidy would silently fail to match
 * the compound roles and render them as literal text, which is the one failure mode that would look
 * like an authoring mistake rather than a host bug.
 *
 * Non-global on purpose — a global regex carries `lastIndex` between calls, and this module is
 * called once per step and once per article in a loop. {@link splitGuidanceProse} and
 * {@link guidanceProseWords} each build their own global copy.
 */
const ANCHOR_TOKEN = /\{anchor:([^}]*)\}/;

function globalToken(): RegExp {
  return new RegExp(ANCHOR_TOKEN.source, 'g');
}

/** A run of authored words, or a control the author named in the middle of them. */
export type GuidanceProsePart =
  | { readonly kind: 'text'; readonly text: string }
  | { readonly kind: 'anchor'; readonly role: string; readonly token: string };

/**
 * Split authored prose into its words and the controls named inside it, in order.
 *
 * Prose carrying no token comes back as a single `text` part, so the ordinary case — which is every
 * article authored before this existed — is one allocation and renders exactly as it did before.
 *
 * A token with an EMPTY role (`{anchor:}`) is not a reference to anything, so it is returned as
 * `text` and will draw as the literal characters the author typed. That is deliberate: see
 * {@link GuidanceProsePart} consumers for why an unresolvable token is shown rather than hidden.
 */
export function splitGuidanceProse(text: string): readonly GuidanceProsePart[] {
  const re = globalToken();
  const parts: GuidanceProsePart[] = [];
  let at = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const role = (m[1] ?? '').trim();
    if (role === '') continue; // not a reference; leave it in the surrounding text run
    if (m.index > at) parts.push({ kind: 'text', text: text.slice(at, m.index) });
    parts.push({ kind: 'anchor', role, token: m[0] });
    at = m.index + m[0].length;
  }
  if (at < text.length) parts.push({ kind: 'text', text: text.slice(at) });
  return parts;
}

/**
 * The same prose as a search subject: the author's words, with the tokens taken out.
 *
 * The token is removed ENTIRELY rather than replaced by a space, and then runs of whitespace are
 * collapsed. Both halves matter and each fixes a different miss:
 *
 *   'the grid {anchor:app-launcher} opens it'  →  'the grid opens it'   (a space would leave two)
 *   'click the grid icon{anchor:app-launcher}.' →  'click the grid icon.' (a space would orphan the full stop)
 *
 * Without this a reader searching for a phrase that spans a glyph gets nothing, and the better the
 * prose is annotated the less of it is findable — the search would punish the feature.
 */
export function guidanceProseWords(text: string): string {
  return text.replace(globalToken(), '').replace(/\s+/g, ' ').trim();
}

/**
 * Every anchor role this prose names, for the projection that decides which anchors travel with a
 * guide.
 *
 * This exists because of a defect its own test caught. `projectGuidance` sends a package's guide
 * only the anchors its recipe STEPS name — deliberately, so an org's unrelated anchors do not ride
 * along — and an anchor named only inside a sentence was therefore not sent. The token resolved
 * against a map that never contained it, so an article could glyph a control only when some recipe
 * step happened to name the same one, and an article in a package with no recipes could glyph
 * nothing at all. The rendering was right and the control was simply not there.
 */
export function guidanceProseRoles(text: string): readonly string[] {
  return splitGuidanceProse(text).flatMap((p) => (p.kind === 'anchor' ? [p.role] : []));
}
